import React, { useContext, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import ProjectHeader from "../../_shared/ProjectHeader/ProjectHeader";
import { setDynamicBreadcrumbs } from "../../_shared/_redux/ProjectActions";
import { HeaderContext } from "../../../../HeaderContext";
import { breadCrumbsArray } from "../../../../data/breadCrumbs";
import {
  QUARTERLY_OKR,
  QUARTERLY_OKR_SUMMARY,
} from "../../../../data/RouteUrls";
import {
  briefRoute,
  quarterCrumbs,
  quarterRowOf,
} from "../_config/quarterlyOkrRoutes";
import AnnualOkrReviewSkeleton from "../../AnnualOkr/AnnualOkrReview/AnnualOkrReviewSkeleton";
import {
  SUMMARY_STATUS,
  objectiveKey,
  readError,
} from "../_config/quarterlyOkrConfig";
import quarterlyOkrStore, {
  useQuarterlyOkrStore,
} from "../_store/quarterlyOkrStore";
import QokrBanner from "../_shared/QokrBanner/QokrBanner";
import QokrCoachRemarks from "../_shared/QokrCoachRemarks/QokrCoachRemarks";
import SummaryObjectives from "./SummaryObjectives/SummaryObjectives";
import SummaryNotSubmitted from "./SummaryNotSubmitted/SummaryNotSubmitted";
import "../../AnnualOkr/AnnualOkrReview/AnnualOkrReview.scss";
import "./_quarterly-okr-summary.scss";

// ═══════════════════════════════════════════════════════════════════════════
// `/coach-panel/quarterly-okrs/:quarterCompanyId/summary` — a locked, frozen
// or archived quarter (§12), read only. The body (`SummaryObjectives`,
// `SummaryNotSubmitted`, `QokrCoachRemarks`) is the CEO app's summary, so
// both sides describe the quarter the same way; the coach's copy always
// carries `not_submitted` and `coach_review`.
// ═══════════════════════════════════════════════════════════════════════════

const selectSummaries = (state) => state.summaryByQc;
const selectClients = (state) => state.clients;

const STATUS_BANNER = {
  [SUMMARY_STATUS.LOCKED]: {
    tone: "success",
    icon: "fa-solid fa-lock",
    title: "Locked",
  },
  [SUMMARY_STATUS.FROZEN]: {
    tone: "warn",
    icon: "fa-solid fa-snowflake",
    title: "Not completed",
  },
  [SUMMARY_STATUS.IN_PROGRESS]: {
    tone: "info",
    icon: "fa-solid fa-hourglass-half",
    title: "Planning is still open",
  },
  [SUMMARY_STATUS.ARCHIVED]: {
    tone: "info",
    icon: "fa-solid fa-box-archive",
    title: "Archived quarter",
  },
  [SUMMARY_STATUS.NOT_STARTED]: {
    tone: "info",
    icon: "fa-regular fa-calendar-xmark",
    title: "Not started",
  },
};

const QuarterlyOkrSummary = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const HeaderMetaData = useContext(HeaderContext);
  const { quarterCompanyId: qcId } = useParams();
  const summary = useQuarterlyOkrStore(selectSummaries)[qcId];
  const clients = useQuarterlyOkrStore(selectClients);
  const [loadError, setLoadError] = useState(null);
  const [activeKey, setActiveKey] = useState(null);

  // The summary has no company name; the dashboard row does.
  const quarterRow = quarterRowOf(clients, qcId);
  const client = (clients || []).find((c) =>
    (c.quarters || []).some(
      (q) => String(q.quarter_company_id) === String(qcId),
    ),
  );
  const quarter = client?.quarters.find(
    (q) => String(q.quarter_company_id) === String(qcId),
  );

  useEffect(() => {
    document.title = "Art of Scale | Quarterly OKR Summary";
    HeaderMetaData.setHeaderMetadata({ headerTitle: "Quarterly OKRs" });
    if (!quarterlyOkrStore.getState().clients) {
      quarterlyOkrStore
        .getState()
        .fetchDashboard()
        .catch(() => null);
    }
    quarterlyOkrStore
      .getState()
      .fetchSummary(qcId)
      .then(() => setLoadError(null))
      .catch((error) => setLoadError(readError(error).message));
    // eslint-disable-next-line
  }, [qcId]);

  useEffect(() => {
    dispatch(
      setDynamicBreadcrumbs(
        QUARTERLY_OKR_SUMMARY,
        quarterCrumbs({
          root: breadCrumbsArray[QUARTERLY_OKR][0],
          qcId,
          row: quarterRow,
          companyName: client?.company_name,
          title: "Summary",
          fallbackTitle: "Quarter summary",
        }),
      ),
    );
  }, [dispatch, client?.company_name, quarterRow, qcId]);

  const marks = useMemo(
    () =>
      Object.fromEntries(
        (summary?.coach_review?.marks || []).map((mark) => [
          objectiveKey(mark.type, mark.objective_id),
          mark,
        ]),
      ),
    [summary],
  );

  const banner = STATUS_BANNER[summary?.status];
  const coachReview = summary?.coach_review;
  const hasCoachReview = Boolean(
    coachReview?.marks?.length || coachReview?.comments?.length,
  );

  return (
    <div>
      <ProjectHeader
        handleNavigation={() =>
          history.push(
            quarterRow?.brief_status ? briefRoute(qcId) : QUARTERLY_OKR,
          )
        }
        showBack
      />
      <div id="annual-okr-review" className="qokr-coach-summary">
        <div className="aokr-header">
          <div className="aokr-title">
            Quarter summary
            {client?.company_name ? ` — ${client.company_name}` : ""}
            {quarter?.label ? (
              <span className="qokr-coach-summary__quarter">
                {quarter.label}
              </span>
            ) : null}
          </div>
        </div>
        <div className="aokr-divider" />

        {loadError && !summary ? (
          <div className="aokr-empty">{loadError}</div>
        ) : !summary ? (
          <AnnualOkrReviewSkeleton />
        ) : (
          <div className="qokr-coach-summary__body">
            {banner ? (
              <QokrBanner
                tone={banner.tone}
                icon={banner.icon}
                title={banner.title}
              >
                {summary.message}
              </QokrBanner>
            ) : null}

            {summary.status !== SUMMARY_STATUS.NOT_STARTED ? (
              <div
                className={`qokr-coach-summary__grid${
                  hasCoachReview ? " qokr-coach-summary__grid--with-aside" : ""
                }`}
              >
                <div className="qokr-coach-summary__main">
                  <SummaryObjectives
                    companyObjectives={summary.company_objectives}
                    individualObjectives={summary.individual_objectives}
                    marks={marks}
                    activeKey={activeKey}
                  />
                  <SummaryNotSubmitted people={summary.not_submitted} />
                </div>
                {hasCoachReview ? (
                  <aside className="qokr-coach-summary__aside">
                    <QokrCoachRemarks
                      remarks={coachReview}
                      onFocus={setActiveKey}
                    />
                  </aside>
                ) : null}
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

export default QuarterlyOkrSummary;
